import { useEffect, useMemo, useRef, useState } from 'react';
import { AppShell } from '../../components/AppShell';
import { Skeleton, SkeletonLine } from '../../components/Skeleton';
import { Badge, Button, Card, FormField, Input } from '../../design';
import { useMe, useModuleEnabled } from '../../lib/auth';
import {
  prepStatusCopy,
  resolveWarehouseFacility,
  useClaimWarehousePrepBatch,
  useCreateWarehousePrepBox,
  useCreateWarehousePrepWorkCharge,
  useFinalizeWarehousePrepBox,
  useMapWarehousePrepLine,
  useReopenWarehousePrepBox,
  useReplaceWarehousePrepBoxContents,
  useWarehouseCartons,
  useWarehouseFacilities,
  useWarehousePrepBatch,
  useWarehousePrepBatches,
  useWarehousePrepBoxes,
  useWarehousePrepAmazonHandoff,
  useSyncWarehousePrepAmazonHandoff,
  useConfirmWarehousePrepAmazonHandoff,
  useRetryWarehousePrepAmazonLabels,
  useDispatchWarehousePrepBatch,
  useWarehousePrepCartonRecommendations,
  useWarehouseServices,
  useWarehouseWorkCharges,
  useReverseWarehouseWorkCharge,
  useWarehouseIdempotencyKey,
  downloadWarehousePrepAmazonLabel,
  createIdempotencyKey,
  rememberWarehousePrepDispatch,
  readWarehousePrepDispatch,
  rememberWarehousePrepDispatchRequest,
  readWarehousePrepDispatchRequest,
  forgetWarehousePrepDispatchRequest,
  type WarehousePrepDispatchPresentation,
} from '../../lib/warehouse';
import type {
  CreateWarehousePrepWorkChargeRequest,
  WarehouseCarton,
  WarehouseCartonRecommendationResponse,
  WarehousePrepBatchDetail,
  WarehousePrepBox,
  WarehouseWorkChargeResponse,
  PrepAmazonHandoffResponse,
  WarehousePrepDispatchResponse,
  DispatchWarehousePrepBatchRequest,
} from '@sourcito/schemas';

const CONFLICT_NOTICE =
  'This prep batch changed on the server. Latest data loaded; your unsaved entries were kept. Review them before retrying.';

type BoxDraft = Record<string, Record<string, string>>;
type MeasurementDraft = Record<
  string,
  { cartonId: string; length: string; width: string; height: string; weight: string }
>;
type MappingDraft = Record<
  string,
  { sellerSku: string; asin: string; fnsku: string; units: string }
>;
type DispatchRequest = Omit<DispatchWarehousePrepBatchRequest, 'idempotencyKey'> & {
  idempotencyKey: string;
};
type DispatchResponse = WarehousePrepDispatchResponse | WarehousePrepDispatchPresentation;

const DEFAULT_WAREHOUSE_MARKETPLACE_ID = 'ATVPDKIKX0DER';

function canUsePrep(role: string | undefined, warehouseEnabled: boolean) {
  return role === 'warehouse' || ((role === 'owner' || role === 'operator') && warehouseEnabled);
}

function readableMappingBlocker(label: string) {
  return `${label} needs a reviewed Seller SKU and pack size before it can be sent to Amazon.`;
}

function numberOrZero(value: string | undefined) {
  const number = Number(value ?? '0');
  return Number.isInteger(number) && number > 0 ? number : 0;
}

export function measurementMicrometers(value: string) {
  const parts = value.trim().split('.');
  if (parts.length > 2) return 0;
  const [whole, fraction = ''] = parts;
  if (!/^\d+$/.test(whole ?? '') || !/^\d*$/.test(fraction) || fraction.length > 4) return 0;
  return Number(BigInt(whole ?? '0') * 10_000n + BigInt((fraction + '0000').slice(0, 4)));
}

export function measurementMilligrams(value: string) {
  const parts = value.trim().split('.');
  if (parts.length > 2) return 0;
  const [whole, fraction = ''] = parts;
  if (!/^\d+$/.test(whole ?? '') || !/^\d*$/.test(fraction) || fraction.length > 6) return 0;
  return Number(BigInt(whole ?? '0') * 1_000_000n + BigInt((fraction + '000000').slice(0, 6)));
}

export function formatAmazonCartonEvidence(
  dimensionsUm: [string, string, string] | null,
  weightMg: string | null
) {
  const format = (value: number) => Number(value.toFixed(2)).toString();
  const dimensions = dimensionsUm?.map((value) => format(Number(value) / 25_400));
  const weight = weightMg ? format(Number(weightMg) / 453_592.37) : null;
  if (dimensions && weight) return `Amazon carton: ${dimensions.join(' × ')} in · ${weight} lb`;
  if (dimensions) return `Amazon carton dimensions: ${dimensions.join(' × ')} in`;
  return weight ? `Amazon carton weight: ${weight} lb` : null;
}

function unitString(value: number, divisor: number, digits: number) {
  const whole = Math.trunc(value / divisor);
  const remainder = Math.abs(value % divisor);
  if (!remainder) return String(whole);
  return `${whole}.${String(remainder).padStart(digits, '0').replace(/0+$/, '')}`;
}

function safeCartonDimension(value: string) {
  const raw = BigInt(value);
  return `${raw / 10_000n}.${String(raw % 10_000n).padStart(4, '0')}`
    .replace(/(\.\d*?)0+$/, '$1')
    .replace(/\.$/, '');
}

export function worksheet(
  batch: WarehousePrepBatchDetail,
  boxes: WarehousePrepBox[],
  cartons: Array<Pick<WarehouseCarton, 'id' | 'code'>> = []
) {
  const linesById = new Map(batch.lines.map((line) => [line.id, line]));
  const distinctSkuCount = new Set(batch.lines.map((line) => line.product.sellerSku ?? line.id))
    .size;
  const vendorUnits = batch.lines.reduce((total, line) => total + line.vendorUnits, 0);
  const listingUnits = batch.lines.reduce((total, line) => total + (line.listingUnits ?? 0), 0);
  const lines = [
    `Prep ID: ${batch.reference}`,
    `Facility: ${batch.facility.name}`,
    `Distinct SKUs: ${distinctSkuCount}`,
    `Vendor units: ${vendorUnits}`,
    `Listing units: ${listingUnits}`,
    `Box count: ${boxes.length}`,
    '',
    'In Seller Central, start one Send to Amazon workflow. Use Prep ID PB-… as the workflow name, add all items and all boxes below, then complete placement there. Do not create one workflow per box.',
    '',
  ];
  for (const box of boxes) {
    const measurements = box.finalization?.measurements;
    lines.push(`Box: ${box.boxNumber}`);
    const carton = cartons.find((item) => item.id === box.finalization?.cartonId);
    if (box.finalization) lines.push(`  Carton: ${carton?.code ?? 'Selected carton'}`);
    if (measurements) {
      lines.push(
        `  Dimensions: ${unitString(measurements.lengthUm, 10_000, 4)} × ${unitString(measurements.widthUm, 10_000, 4)} × ${unitString(measurements.heightUm, 10_000, 4)} cm; gross weight: ${unitString(measurements.weightMg, 1_000_000, 6)} kg`
      );
      if (box.finalization?.packingStatus === 'provisional')
        lines.push('  Warning: provisional measurements — verify before submitting to Amazon.');
    }
    for (const content of box.contents) {
      const line = linesById.get(content.prepLineId);
      lines.push(
        `  SKU: ${content.sellerSku}; vendor item: ${line?.product.vendorItemNo ?? 'Not supplied'}; listing units: ${content.listingUnits}; vendor units: ${content.vendorUnits}; pack size: ${content.unitsPerListing}`
      );
    }
  }
  return lines.join('\n');
}

async function copyText(value: string) {
  if (!navigator.clipboard?.writeText) throw new Error('Clipboard is unavailable in this browser.');
  await navigator.clipboard.writeText(value);
}

export function buildBoxContents(
  batch: WarehousePrepBatchDetail,
  boxes: WarehousePrepBox[],
  box: WarehousePrepBox,
  draft: BoxDraft
):
  | {
      contents: Array<{
        prepLineId: string;
        lotId: string;
        vendorUnits: number;
        listingUnits: number;
      }>;
    }
  | { error: string } {
  const values = draft[box.id] ?? {};
  const reservations = new Map<string, number>();
  for (const other of boxes) {
    if (other.id === box.id) continue;
    for (const content of other.contents)
      reservations.set(
        content.prepLineId,
        (reservations.get(content.prepLineId) ?? 0) + content.vendorUnits
      );
  }
  const contents = [];
  for (const line of batch.lines) {
    const existing = box.contents.find((content) => content.prepLineId === line.id);
    const raw = values[line.id] ?? (existing ? String(existing.vendorUnits) : '');
    if (!raw.trim()) continue;
    if (!/^\d+$/.test(raw))
      return { error: `${line.product.productLabel}: enter a whole vendor-unit quantity.` };
    const vendorUnits = Number(raw);
    const available = line.vendorUnits - (reservations.get(line.id) ?? 0);
    if (!Number.isSafeInteger(vendorUnits) || vendorUnits <= 0 || vendorUnits > available)
      return {
        error: `${line.product.productLabel}: enter a whole quantity from 1 to ${Math.max(available, 0)}.`,
      };
    const unitsPerListing = line.product.unitsPerListing;
    if (!unitsPerListing || vendorUnits % unitsPerListing !== 0)
      return { error: `${line.product.productLabel}: quantity must match its reviewed pack size.` };
    contents.push({
      prepLineId: line.id,
      lotId: line.lotId,
      vendorUnits,
      listingUnits: vendorUnits / unitsPerListing,
    });
  }
  return { contents };
}

function errorCode(error: unknown) {
  if (!(error instanceof Error) || !('details' in error)) return null;
  const details = error.details;
  return details && typeof details === 'object' && 'error' in details
    ? ((details.error as { code?: string }).code ?? null)
    : null;
}

function humanPrepError(error: unknown) {
  if (errorCode(error) === 'PREP_REOPEN_BLOCKED')
    return 'This box cannot be reopened until its related charge or invoice is resolved.';
  return error instanceof Error ? error.message : 'The server could not complete this action.';
}

function humanHandoffError(error: unknown) {
  switch (errorCode(error)) {
    case 'CREDENTIAL_MISSING':
      return 'Amazon connection is not set up for this workspace. Add an Amazon credential, then try again.';
    case 'AMAZON_READ_FAILED':
      return 'Amazon could not be checked right now. Try again.';
    case 'AMAZON_LABEL_FAILED':
      return 'Amazon could not fetch this label. Try the label again.';
    default:
      return 'Amazon handoff could not be completed. Try again.';
  }
}

function isDeterministicDispatchFailure(error: unknown) {
  if (!error || typeof error !== 'object' || !('status' in error)) return false;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' && status >= 400 && status < 500;
}

export function WarehousePrepPage() {
  const { data: me, isLoading: meLoading, isError: meError, refetch: refetchMe } = useMe();
  const warehouseEnabled = useModuleEnabled('warehouse');
  const amazonEnabled = useModuleEnabled('amazon');
  const permitted = Boolean(me && canUsePrep(me.user.role, warehouseEnabled));
  const facilities = useWarehouseFacilities(permitted);
  const [facilityId, setFacilityId] = useState<string | null>(null);
  const visibleFacilities = facilities.data ?? [];
  const selectedFacility = resolveWarehouseFacility(visibleFacilities, facilityId);
  const requestedReference = useMemo(
    () =>
      typeof window === 'undefined'
        ? null
        : new URLSearchParams(window.location.search).get('prep'),
    []
  );
  // A Prep ID is globally meaningful to this tenant. Load its visible queue before a worker
  // chooses a facility, rather than accidentally selecting the first item at another facility.
  const batches = useWarehousePrepBatches(
    requestedReference ? undefined : selectedFacility?.id,
    permitted && (Boolean(requestedReference) || Boolean(selectedFacility))
  );
  const requestedSummary = requestedReference
    ? (batches.data?.find((candidate) => candidate.reference === requestedReference) ?? null)
    : null;
  const selectedSummary = requestedReference ? requestedSummary : (batches.data?.[0] ?? null);
  const activeFacility = requestedSummary?.facility ?? selectedFacility;
  const batch = useWarehousePrepBatch(selectedSummary?.id, Boolean(selectedSummary));
  const boxes = useWarehousePrepBoxes(selectedSummary?.id, Boolean(selectedSummary));
  const handoff = useWarehousePrepAmazonHandoff(
    selectedSummary?.id,
    permitted && amazonEnabled && Boolean(selectedSummary)
  );
  const cartons = useWarehouseCartons(activeFacility?.id, Boolean(activeFacility));
  const services = useWarehouseServices(activeFacility?.id, Boolean(activeFacility));
  const charges = useWarehouseWorkCharges(
    { warehouseId: activeFacility?.id },
    Boolean(activeFacility)
  );
  const claim = useClaimWarehousePrepBatch();
  const createBox = useCreateWarehousePrepBox();
  const replaceContents = useReplaceWarehousePrepBoxContents();
  const finalizeBox = useFinalizeWarehousePrepBox();
  const mapLine = useMapWarehousePrepLine();
  const reopenBox = useReopenWarehousePrepBox();
  const createCharge = useCreateWarehousePrepWorkCharge();
  const reverseCharge = useReverseWarehouseWorkCharge();
  const recommendations = useWarehousePrepCartonRecommendations();
  const syncHandoff = useSyncWarehousePrepAmazonHandoff();
  const confirmHandoff = useConfirmWarehousePrepAmazonHandoff();
  const retryLabels = useRetryWarehousePrepAmazonLabels();
  const dispatch = useDispatchWarehousePrepBatch();
  const [boxDraft, setBoxDraft] = useState<BoxDraft>({});
  const [mappingDraft, setMappingDraft] = useState<MappingDraft>({});
  const [measurementDraft, setMeasurementDraft] = useState<MeasurementDraft>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [copyNotice, setCopyNotice] = useState<string | null>(null);
  const [recommendation, setRecommendation] = useState<WarehouseCartonRecommendationResponse>();
  const [actionError, setActionError] = useState<string | null>(null);
  const [dispatchResponse, setDispatchResponse] = useState<DispatchResponse>();
  const [dispatchRequest, setDispatchRequest] = useState<DispatchRequest>();

  const handleCopy = async (value: string, success: string) => {
    try {
      await copyText(value);
      setCopyNotice(success);
    } catch (error) {
      setCopyNotice(
        error instanceof Error ? `Could not copy: ${error.message}` : 'Could not copy to clipboard.'
      );
    }
  };

  useEffect(() => {
    if (requestedSummary) setFacilityId(requestedSummary.facility.id);
  }, [requestedSummary]);

  useEffect(() => {
    if (!boxes.data) return;
    setBoxDraft((current) => {
      let changed = false;
      const next = { ...current };
      for (const box of boxes.data) {
        const values = { ...(next[box.id] ?? {}) };
        for (const content of box.contents) {
          if (values[content.prepLineId] === undefined) {
            values[content.prepLineId] = String(content.vendorUnits);
            changed = true;
          }
        }
        if (changed || next[box.id]) next[box.id] = values;
      }
      return changed ? next : current;
    });
  }, [boxes.data]);

  useEffect(() => {
    setDispatchResponse(
      selectedSummary?.reference
        ? readWarehousePrepDispatch(me?.tenant?.id, selectedSummary.reference)
        : undefined
    );
    setDispatchRequest(
      selectedSummary?.reference
        ? readWarehousePrepDispatchRequest(me?.tenant?.id, selectedSummary.reference)
        : undefined
    );
  }, [selectedSummary?.id]);

  const refreshPrepState = () => {
    void batches.refetch?.();
    void batch.refetch?.();
    void boxes.refetch?.();
    void charges.refetch?.();
  };
  const onMutationError = (error: unknown) => {
    if (
      ['PREP_CONFLICT', 'PREP_BOX_CONFLICT', 'PREP_BATCH_CONFLICT'].includes(errorCode(error) ?? '')
    ) {
      setRecommendation(undefined);
      setNotice(CONFLICT_NOTICE);
      refreshPrepState();
      return;
    }
    setActionError(humanPrepError(error));
  };
  const detail = batch.data;
  const prepBoxes = boxes.data ?? [];
  const prepCharges = (charges.data ?? []).filter(
    (item) => item.charge.sourcePrepBatchId === detail?.id
  );

  if (meError) {
    return (
      <AccessState title="Could not check warehouse access" onRetry={() => void refetchMe()} />
    );
  }
  if (meLoading || !me) return <AccessState title="Checking warehouse access…" loading />;
  if (!permitted)
    return (
      <AccessState
        title="Warehouse prep unavailable"
        detail="This account is not granted access to prep work."
      />
    );
  if (facilities.isLoading && !requestedReference)
    return <AccessState title="Loading warehouse facilities…" loading />;
  if (facilities.isError && !requestedReference)
    return (
      <AccessState
        title="Could not load warehouse facilities"
        onRetry={() => void facilities.refetch()}
      />
    );
  if (!facilities.data?.length && !requestedReference)
    return (
      <AccessState
        title="No warehouse facilities"
        detail="Add or assign a facility before starting prep work."
      />
    );

  return (
    <AppShell>
      <main className="max-w-6xl mx-auto px-6 sm:px-8 py-8 space-y-6">
        <header className="space-y-2">
          <p className="text-xs uppercase tracking-[0.18em] text-fg-tertiary">WAREHOUSE PREP</p>
          <h1 className="text-2xl font-semibold text-fg-primary">Prep workstation</h1>
          <p className="text-sm text-fg-secondary">
            Build physical boxes before creating one manual Send to Amazon workflow.
          </p>
        </header>

        {visibleFacilities.length > 1 && (
          <FormField label="Warehouse facility" htmlFor="prep-facility">
            <select
              id="prep-facility"
              value={selectedFacility?.id ?? ''}
              onChange={(event) => setFacilityId(event.target.value || null)}
              className="w-full rounded-md border border-border-subtle bg-surface px-3 py-2 text-sm"
            >
              <option value="">Select a facility</option>
              {visibleFacilities.map((facility) => (
                <option key={facility.id} value={facility.id}>
                  {facility.name}
                </option>
              ))}
            </select>
          </FormField>
        )}

        {!activeFacility ? (
          <Card>
            <p className="text-sm text-fg-secondary">
              {requestedReference
                ? batches.isLoading
                  ? 'Loading prep batch…'
                  : batches.isError
                    ? 'Could not load this prep batch.'
                    : `Prep batch ${requestedReference} was not found.`
                : 'Select a facility to see prep work.'}
            </p>
            {requestedReference && batches.isError && (
              <Button type="button" className="mt-3" onClick={() => void batches.refetch()}>
                Retry
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[minmax(16rem,0.8fr)_minmax(0,2fr)]">
            <section aria-labelledby="prep-queue-heading" className="space-y-3">
              <h2 id="prep-queue-heading" className="text-lg font-medium">
                Prep queue
              </h2>
              {batches.isLoading && <PrepQueueSkeleton />}
              {batches.isError && (
                <RetryCopy
                  message="Could not load prep batches."
                  onRetry={() => void batches.refetch()}
                />
              )}
              {batches.data?.length === 0 && (
                <Card>
                  <p className="text-sm text-fg-secondary">
                    No prep batches are waiting at this facility.
                  </p>
                </Card>
              )}
              {batches.data?.map((item) => (
                <a
                  key={item.id}
                  href={`/app/warehouse/prep?prep=${encodeURIComponent(item.reference)}`}
                  className="block rounded-lg border border-border-subtle bg-surface p-4 hover:border-accent-primary focus:outline-none focus:ring-2 focus:ring-accent-primary"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{item.reference}</span>
                    <Badge variant="neutral">{item.status.replaceAll('_', ' ')}</Badge>
                  </div>
                  <p className="mt-2 text-sm text-fg-secondary">
                    {prepStatusCopy(item.status, item.remainingVendorUnits)}
                  </p>
                  <p className="mt-1 text-xs text-fg-tertiary">
                    {item.facility.name} · {item.lineCount} items
                  </p>
                </a>
              ))}
            </section>

            <section aria-live="polite" aria-atomic="true" className="space-y-5">
              {notice && (
                <div
                  role="alert"
                  className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm text-fg-primary"
                >
                  {notice}
                </div>
              )}
              {actionError && (
                <div
                  role="alert"
                  className="rounded-md border border-negative/40 bg-negative/10 p-3 text-sm"
                >
                  {actionError}
                </div>
              )}
              {!selectedSummary && !batches.isLoading && (
                <Card>
                  <p className="text-sm text-fg-secondary">Choose a prep batch to start.</p>
                </Card>
              )}
              {batch.isLoading && <BatchDetailSkeleton />}
              {batch.isError && (
                <RetryCopy
                  message="Could not load this prep batch."
                  onRetry={() => void batch.refetch()}
                />
              )}
              {detail && (
                <>
                  <Card className="space-y-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h2 className="text-xl font-semibold">{detail.reference}</h2>
                        <p className="mt-1 text-sm text-fg-secondary">
                          {prepStatusCopy(detail.status, detail.remainingVendorUnits)}
                        </p>
                      </div>
                      <Button
                        type="button"
                        onClick={() => void handleCopy(detail.reference, 'Prep ID copied.')}
                      >
                        Copy Prep ID
                      </Button>
                    </div>
                    {copyNotice && (
                      <p
                        role="status"
                        className={`text-sm ${copyNotice.startsWith('Could not') ? 'text-negative' : 'text-positive'}`}
                      >
                        {copyNotice}
                      </p>
                    )}
                    {detail.status === 'ready_to_prep' && (
                      <Button
                        type="button"
                        disabled={claim.isPending}
                        onClick={() =>
                          claim
                            .mutateAsync({ batchId: detail.id, expectedVersion: detail.version })
                            .catch(onMutationError)
                        }
                      >
                        Claim prep work
                      </Button>
                    )}
                    {detail.status === 'label_ready' || detail.status === 'dispatched' ? (
                      <p className="text-sm text-fg-secondary">This prep record is read-only.</p>
                    ) : null}
                  </Card>

                  <Card>
                    <h3 className="font-medium">Items and mapping checks</h3>
                    <div className="mt-3 space-y-3">
                      {detail.lines.map((line) => (
                        <div key={line.id} className="rounded-md border border-border-subtle p-3">
                          <div className="flex flex-wrap justify-between gap-2">
                            <div>
                              <p className="font-medium">{line.product.productLabel}</p>
                              {line.product.vendorItemNo && (
                                <p className="text-xs text-fg-tertiary">
                                  Vendor item {line.product.vendorItemNo}
                                </p>
                              )}
                            </div>
                            <p className="text-sm text-fg-secondary">
                              {line.remainingVendorUnits} vendor units remaining
                            </p>
                          </div>
                          {line.mappingStatus === 'mapping_required' ? (
                            <MappingForm
                              line={line}
                              batch={detail}
                              draft={mappingDraft[line.id]}
                              onDraftChange={(draft) =>
                                setMappingDraft((current) => ({ ...current, [line.id]: draft }))
                              }
                              onSave={(input) => mapLine.mutateAsync(input).catch(onMutationError)}
                            />
                          ) : (
                            <>
                              <p className="mt-2 text-sm text-positive">
                                Mapped to {line.product.sellerSku} · {line.product.unitsPerListing}{' '}
                                vendor units per listing unit
                              </p>
                              {mappingDraft[line.id] && (
                                <p className="mt-1 text-sm text-warning">
                                  Retained mapping draft:{' '}
                                  {mappingDraft[line.id]!.sellerSku || 'review needed'}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </Card>

                  {(detail.status === 'in_prep' || detail.status === 'awaiting_amazon') &&
                    (boxes.isLoading ? (
                      <BoxBuilderSkeleton />
                    ) : boxes.isError ? (
                      <Card>
                        <RetryCopy
                          message="Could not load prep boxes."
                          onRetry={() => void boxes.refetch()}
                        />
                      </Card>
                    ) : (
                      <BoxBuilder
                        batch={detail}
                        boxes={prepBoxes}
                        boxDraft={boxDraft}
                        setBoxDraft={setBoxDraft}
                        measurementDraft={measurementDraft}
                        setMeasurementDraft={setMeasurementDraft}
                        cartons={cartons.data ?? []}
                        cartonsLoading={cartons.isLoading}
                        cartonsError={cartons.isError}
                        onRetryCartons={() => void cartons.refetch()}
                        editable={detail.status === 'in_prep'}
                        onCreate={() =>
                          createBox.mutateAsync({ batchId: detail.id }).catch(onMutationError)
                        }
                        onSave={(box) => {
                          const prepared = buildBoxContents(detail, prepBoxes, box, boxDraft);
                          if ('error' in prepared) {
                            setActionError(prepared.error);
                            return;
                          }
                          return replaceContents
                            .mutateAsync({
                              batchId: detail.id,
                              boxId: box.id,
                              expectedVersion: box.version,
                              contents: prepared.contents,
                            })
                            .catch(onMutationError);
                        }}
                        onFinalize={(box) => {
                          const draft = measurementDraft[box.id];
                          if (!draft?.cartonId) return;
                          return finalizeBox
                            .mutateAsync({
                              batchId: detail.id,
                              boxId: box.id,
                              expectedVersion: box.version,
                              cartonId: draft.cartonId,
                              measurements: {
                                lengthUm: measurementMicrometers(draft.length),
                                widthUm: measurementMicrometers(draft.width),
                                heightUm: measurementMicrometers(draft.height),
                                weightMg: measurementMilligrams(draft.weight),
                              },
                            })
                            .catch(onMutationError);
                        }}
                        onReopen={(box, reason) =>
                          reopenBox
                            .mutateAsync({
                              batchId: detail.id,
                              boxId: box.id,
                              expectedVersion: box.version,
                              reason,
                            })
                            .catch(onMutationError)
                        }
                      />
                    ))}

                  {detail.status === 'in_prep' && (
                    <RecommendationPanel
                      recommendation={recommendation}
                      isPending={recommendations.isPending}
                      onCompare={() =>
                        recommendations
                          .mutateAsync({
                            batchId: detail.id,
                            marketplaceId: DEFAULT_WAREHOUSE_MARKETPLACE_ID,
                            expectedBatchVersion: detail.version,
                          })
                          .then(setRecommendation)
                          .catch(onMutationError)
                      }
                    />
                  )}

                  {(detail.status === 'awaiting_amazon' ||
                    detail.status === 'label_ready' ||
                    detail.status === 'dispatched') &&
                    (boxes.isLoading || cartons.isLoading ? (
                      <Card className="space-y-4" aria-hidden="true">
                        <div className="space-y-2">
                          <SkeletonLine className="w-40" />
                          <SkeletonLine className="w-72 h-3" />
                        </div>
                        <Skeleton className="h-9 w-48 rounded-md" />
                      </Card>
                    ) : boxes.isError || cartons.isError ? (
                      <Card>
                        <RetryCopy
                          message="Could not load packing worksheet data."
                          onRetry={() => {
                            void boxes.refetch();
                            void cartons.refetch();
                          }}
                        />
                      </Card>
                    ) : (
                      <Handoff
                        batch={detail}
                        boxes={prepBoxes}
                        handoff={handoff.data}
                        loading={handoff.isLoading}
                        error={handoff.isError}
                        syncPending={syncHandoff.isPending}
                        confirmPending={confirmHandoff.isPending}
                        retryPending={retryLabels.isPending}
                        dispatchPending={dispatch.isPending}
                        dispatchResponse={dispatchResponse}
                        dispatchRequest={dispatchRequest}
                        batchRefreshing={batch.isFetching}
                        handoffRefreshing={handoff.isFetching}
                        amazonEnabled={amazonEnabled}
                        onRetryLoad={() => void handoff.refetch()}
                        onSync={() =>
                          syncHandoff.mutateAsync({ batchId: detail.id }).then((result) => {
                            void handoff.refetch();
                            return result;
                          })
                        }
                        onConfirm={(expectedVersion, choiceToken) =>
                          confirmHandoff
                            .mutateAsync({ batchId: detail.id, expectedVersion, choiceToken })
                            .then((result) => {
                              void handoff.refetch();
                              return result;
                            })
                        }
                        onRetryLabel={(boxReference) =>
                          retryLabels
                            .mutateAsync({
                              batchId: detail.id,
                              pageType: 'PackageLabel_Letter_4',
                              boxReference,
                            })
                            .then((result) => {
                              void handoff.refetch();
                              return result;
                            })
                        }
                        onDownloadLabel={(boxReference) =>
                          downloadWarehousePrepAmazonLabel(detail.id, boxReference)
                        }
                        onCopy={() =>
                          void handleCopy(
                            worksheet(detail, prepBoxes, cartons.data ?? []),
                            'Packing worksheet copied.'
                          )
                        }
                        onDispatch={(request) => {
                          setDispatchRequest(request);
                          setDispatchResponse(undefined);
                          rememberWarehousePrepDispatchRequest(
                            me?.tenant?.id,
                            detail.reference,
                            request
                          );
                          return dispatch
                            .mutateAsync({ batchId: detail.id, ...request })
                            .then((result) => {
                              rememberWarehousePrepDispatch(
                                me?.tenant?.id,
                                detail.reference,
                                result
                              );
                              setDispatchResponse(result);
                              setDispatchRequest(undefined);
                              forgetWarehousePrepDispatchRequest(me?.tenant?.id, detail.reference);
                              return result;
                            })
                            .catch((error) => {
                              if (isDeterministicDispatchFailure(error)) {
                                setDispatchRequest(undefined);
                                forgetWarehousePrepDispatchRequest(
                                  me?.tenant?.id,
                                  detail.reference
                                );
                              }
                              throw error;
                            });
                        }}
                      />
                    ))}
                  {(detail.status === 'in_prep' || detail.status === 'awaiting_amazon') &&
                    (services.isLoading || boxes.isLoading ? (
                      <ChargePanelSkeleton />
                    ) : services.isError || boxes.isError ? (
                      <Card>
                        <RetryCopy
                          message="Could not load prep-charge controls."
                          onRetry={() => {
                            void services.refetch();
                            void boxes.refetch();
                          }}
                        />
                      </Card>
                    ) : (
                      <ChargePanel
                        batch={detail}
                        boxes={prepBoxes}
                        services={services.data ?? []}
                        pending={createCharge.isPending}
                        onSubmit={(input) =>
                          createCharge
                            .mutateAsync({ batchId: detail.id, ...input })
                            .catch((error) => {
                              onMutationError(error);
                              throw error;
                            })
                        }
                      />
                    ))}
                  {(detail.status === 'in_prep' || detail.status === 'awaiting_amazon') &&
                    (charges.isLoading ? (
                      <ChargeHistorySkeleton />
                    ) : charges.isError ? (
                      <Card>
                        <RetryCopy
                          message="Could not load prep-charge history."
                          onRetry={() => void charges.refetch()}
                        />
                      </Card>
                    ) : (
                      <ChargeHistory
                        charges={prepCharges}
                        pending={reverseCharge.isPending}
                        onReverse={(chargeId, idempotencyKey, reason) =>
                          reverseCharge
                            .mutateAsync({ chargeId, idempotencyKey, reason })
                            .catch(onMutationError)
                        }
                      />
                    ))}
                </>
              )}
            </section>
          </div>
        )}
      </main>
    </AppShell>
  );
}

function AccessState({
  title,
  detail,
  onRetry,
  loading,
}: {
  title: string;
  detail?: string;
  onRetry?: () => void;
  /** True for the two transient "checking access"/"loading facilities"
   *  calls — swaps the title text for a shimmer line since there's nothing
   *  meaningful to read yet. */
  loading?: boolean;
}) {
  return (
    <AppShell>
      <main className="max-w-xl mx-auto px-6 py-12">
        <Card>
          {loading ? (
            <SkeletonLine className="w-56 h-5" />
          ) : (
            <h1 className="text-xl font-medium">{title}</h1>
          )}
          {detail && <p className="mt-2 text-sm text-fg-secondary">{detail}</p>}
          {onRetry && (
            <Button type="button" className="mt-4" onClick={onRetry}>
              Retry
            </Button>
          )}
        </Card>
      </main>
    </AppShell>
  );
}
function RetryCopy({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <p className="text-sm text-negative">
      {message}{' '}
      <button type="button" className="underline" onClick={onRetry}>
        Retry
      </button>
    </p>
  );
}

/** Shimmer placeholder for the prep queue — mirrors each batch link's
 *  `rounded-lg border p-4` card (reference + status badge, status line). */
function PrepQueueSkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="rounded-lg border border-border-subtle bg-surface p-4"
          aria-hidden="true"
        >
          <div className="flex items-center justify-between gap-2">
            <SkeletonLine className="w-24" />
            <Skeleton className="h-5 w-20 rounded-sm" />
          </div>
          <SkeletonLine className="mt-2 w-40 h-3" />
        </div>
      ))}
    </>
  );
}

/** Shimmer placeholder for the batch-detail section — mirrors the header
 *  Card (reference + status, action button) and the "Items and mapping
 *  checks" Card once `detail` resolves. */
function BatchDetailSkeleton() {
  return (
    <>
      <Card className="space-y-3" aria-hidden="true">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-2">
            <SkeletonLine className="w-32 h-5" />
            <SkeletonLine className="w-56" />
          </div>
          <Skeleton className="h-9 w-32 rounded-md shrink-0" />
        </div>
      </Card>
      <Card aria-hidden="true">
        <h3 className="font-medium">Items and mapping checks</h3>
        <div className="mt-3 space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="rounded-md border border-border-subtle p-3 space-y-2">
              <div className="flex flex-wrap justify-between gap-2">
                <SkeletonLine className="w-40" />
                <SkeletonLine className="w-32 h-3" />
              </div>
              <SkeletonLine className="w-48 h-3" />
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}

/** Shimmer placeholder for BoxBuilder — mirrors its header (title, subtext,
 *  Create-box button) and a couple of box rows. */
function BoxBuilderSkeleton() {
  return (
    <Card className="space-y-4" aria-hidden="true">
      <div className="flex justify-between gap-3">
        <div className="space-y-2">
          <SkeletonLine className="w-40" />
          <SkeletonLine className="w-64 h-3" />
        </div>
        <Skeleton className="h-9 w-24 rounded-md shrink-0" />
      </div>
      {[0, 1].map((i) => (
        <div key={i} className="rounded-md border border-border-subtle p-4 space-y-3">
          <SkeletonLine className="w-32" />
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
      ))}
    </Card>
  );
}

/** Shimmer placeholder for ChargePanel ("Record prep work") — a title plus
 *  a few form-field-shaped blocks. */
function ChargePanelSkeleton() {
  return (
    <Card className="space-y-3" aria-hidden="true">
      <SkeletonLine className="w-36" />
      <Skeleton className="h-9 w-full rounded-md" />
      <Skeleton className="h-9 w-full rounded-md" />
      <Skeleton className="h-9 w-24 rounded-md" />
    </Card>
  );
}

/** Shimmer placeholder for ChargeHistory — mirrors its title plus a couple
 *  of `rounded-md border p-3` charge rows. */
function ChargeHistorySkeleton() {
  return (
    <Card className="space-y-3" aria-hidden="true">
      <SkeletonLine className="w-56" />
      {[0, 1].map((i) => (
        <div key={i} className="rounded-md border border-border-subtle p-3 space-y-2">
          <SkeletonLine className="w-48" />
          <SkeletonLine className="w-24 h-3" />
        </div>
      ))}
    </Card>
  );
}

function MappingForm({
  line,
  batch,
  draft,
  onDraftChange,
  onSave,
}: {
  line: WarehousePrepBatchDetail['lines'][number];
  batch: WarehousePrepBatchDetail;
  draft: MappingDraft[string] | undefined;
  onDraftChange: (draft: MappingDraft[string]) => void;
  onSave: (input: {
    batchId: string;
    lineId: string;
    sellerSku: string;
    asin: string | null;
    fnsku: string | null;
    unitsPerListing: number;
    expectedVersion: number;
  }) => void;
}) {
  const values = draft ?? { sellerSku: '', asin: '', fnsku: '', units: '' };
  return (
    <div className="mt-2 space-y-2">
      <p className="text-sm text-warning">{readableMappingBlocker(line.product.productLabel)}</p>
      <FormField
        label="Reviewed Seller SKU"
        htmlFor={`mapping-${line.product.vendorItemNo ?? line.product.productLabel}-sku`}
      >
        <Input
          id={`mapping-${line.product.vendorItemNo ?? line.product.productLabel}-sku`}
          value={values.sellerSku}
          onChange={(event) => onDraftChange({ ...values, sellerSku: event.target.value })}
        />
      </FormField>
      <FormField
        label="Vendor units per listing unit"
        htmlFor={`mapping-${line.product.vendorItemNo ?? line.product.productLabel}-units`}
      >
        <Input
          id={`mapping-${line.product.vendorItemNo ?? line.product.productLabel}-units`}
          type="number"
          min="1"
          step="1"
          value={values.units}
          onChange={(event) => onDraftChange({ ...values, units: event.target.value })}
        />
      </FormField>
      <FormField
        label="ASIN (optional)"
        htmlFor={`mapping-${line.product.vendorItemNo ?? line.product.productLabel}-asin`}
      >
        <Input
          id={`mapping-${line.product.vendorItemNo ?? line.product.productLabel}-asin`}
          value={values.asin}
          onChange={(event) => onDraftChange({ ...values, asin: event.target.value })}
        />
      </FormField>
      <FormField
        label="FNSKU (optional)"
        htmlFor={`mapping-${line.product.vendorItemNo ?? line.product.productLabel}-fnsku`}
      >
        <Input
          id={`mapping-${line.product.vendorItemNo ?? line.product.productLabel}-fnsku`}
          value={values.fnsku}
          onChange={(event) => onDraftChange({ ...values, fnsku: event.target.value })}
        />
      </FormField>
      <Button
        type="button"
        disabled={!values.sellerSku.trim() || !numberOrZero(values.units)}
        onClick={() =>
          onSave({
            batchId: batch.id,
            lineId: line.id,
            sellerSku: values.sellerSku.trim(),
            asin: values.asin.trim() || null,
            fnsku: values.fnsku.trim() || null,
            unitsPerListing: numberOrZero(values.units),
            expectedVersion: batch.version,
          })
        }
      >
        Save reviewed mapping
      </Button>
    </div>
  );
}

function BoxBuilder({
  batch,
  boxes,
  boxDraft,
  setBoxDraft,
  measurementDraft,
  setMeasurementDraft,
  cartons,
  cartonsLoading,
  cartonsError,
  onRetryCartons,
  editable,
  onCreate,
  onSave,
  onFinalize,
  onReopen,
}: {
  batch: WarehousePrepBatchDetail;
  boxes: WarehousePrepBox[];
  boxDraft: BoxDraft;
  setBoxDraft: React.Dispatch<React.SetStateAction<BoxDraft>>;
  measurementDraft: MeasurementDraft;
  setMeasurementDraft: React.Dispatch<React.SetStateAction<MeasurementDraft>>;
  cartons: WarehouseCarton[];
  cartonsLoading: boolean;
  cartonsError: boolean;
  onRetryCartons: () => void;
  editable: boolean;
  onCreate: () => void;
  onSave: (box: WarehousePrepBox) => void;
  onFinalize: (box: WarehousePrepBox) => void;
  onReopen: (box: WarehousePrepBox, reason: string) => void;
}) {
  return (
    <Card className="space-y-4">
      <div className="flex justify-between gap-3">
        <div>
          <h3 className="font-medium">Boxes and reservations</h3>
          <p className="text-sm text-fg-secondary">
            Enter whole vendor units. Each quantity must match the reviewed pack size.
          </p>
        </div>
        {editable && (
          <Button type="button" onClick={onCreate}>
            Create box
          </Button>
        )}
      </div>
      {boxes.map((box) => {
        const safe = `prep-${box.boxNumber}`;
        const draft = measurementDraft[box.id];
        const measurementsValid =
          Boolean(draft) &&
          measurementMicrometers(draft!.length) > 0 &&
          measurementMicrometers(draft!.width) > 0 &&
          measurementMicrometers(draft!.height) > 0 &&
          measurementMilligrams(draft!.weight) > 0;
        const canFinalize = Boolean(
          !cartonsLoading && !cartonsError && draft?.cartonId && measurementsValid
        );
        return (
          <div key={box.id} className="rounded-md border border-border-subtle p-4 space-y-3">
            <h4 className="font-medium">{box.boxNumber}</h4>
            {box.status === 'open' && editable && (
              <>
                {batch.lines.map((line, index) => (
                  <FormField
                    key={line.id}
                    label={`${line.product.productLabel} — whole vendor units`}
                    htmlFor={`${safe}-line-${index}`}
                  >
                    <Input
                      id={`${safe}-line-${index}`}
                      type="number"
                      min="0"
                      step="1"
                      value={boxDraft[box.id]?.[line.id] ?? ''}
                      onChange={(event) =>
                        setBoxDraft((current) => ({
                          ...current,
                          [box.id]: { ...current[box.id], [line.id]: event.target.value },
                        }))
                      }
                    />
                    <p className="mt-1 text-xs text-fg-tertiary">
                      Reserved in other boxes:{' '}
                      {boxes
                        .filter((other) => other.id !== box.id)
                        .flatMap((other) => other.contents)
                        .filter((content) => content.prepLineId === line.id)
                        .reduce((total, content) => total + content.vendorUnits, 0)}
                    </p>
                  </FormField>
                ))}
                <Button type="button" onClick={() => onSave(box)}>
                  Save contents
                </Button>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {(['length', 'width', 'height', 'weight'] as const).map((field) => (
                    <FormField
                      key={field}
                      label={
                        field === 'weight'
                          ? 'Gross weight (kg)'
                          : `${field.charAt(0).toUpperCase()}${field.slice(1)} (cm)`
                      }
                      htmlFor={`${safe}-${field}`}
                    >
                      <Input
                        id={`${safe}-${field}`}
                        type="number"
                        min="0"
                        step={field === 'weight' ? '0.000001' : '0.0001'}
                        value={measurementDraft[box.id]?.[field] ?? ''}
                        onChange={(event) =>
                          setMeasurementDraft((current) => ({
                            ...current,
                            [box.id]: {
                              cartonId: current[box.id]?.cartonId ?? '',
                              length: current[box.id]?.length ?? '',
                              width: current[box.id]?.width ?? '',
                              height: current[box.id]?.height ?? '',
                              weight: current[box.id]?.weight ?? '',
                              [field]: event.target.value,
                            },
                          }))
                        }
                      />
                      {measurementDraft[box.id]?.[field] &&
                        (field === 'weight'
                          ? measurementMilligrams(measurementDraft[box.id]![field]) === 0
                          : measurementMicrometers(measurementDraft[box.id]![field]) === 0) && (
                          <p className="mt-1 text-xs text-negative">
                            Enter a positive{' '}
                            {field === 'weight'
                              ? 'kg value with up to 6 decimals.'
                              : 'cm value with up to 4 decimals.'}
                          </p>
                        )}
                    </FormField>
                  ))}
                </div>
                {cartonsLoading ? (
                  <p className="text-sm text-fg-tertiary">Loading cartons…</p>
                ) : cartonsError ? (
                  <RetryCopy message="Could not load cartons." onRetry={onRetryCartons} />
                ) : (
                  <FormField label="Carton type" htmlFor={`${safe}-carton`}>
                    <select
                      id={`${safe}-carton`}
                      value={measurementDraft[box.id]?.cartonId ?? ''}
                      onChange={(event) =>
                        setMeasurementDraft((current) => {
                          const carton = cartons.find((item) => item.id === event.target.value);
                          return {
                            ...current,
                            [box.id]: {
                              cartonId: event.target.value,
                              length: carton
                                ? safeCartonDimension(carton.external.lengthUm)
                                : (current[box.id]?.length ?? ''),
                              width: carton
                                ? safeCartonDimension(carton.external.widthUm)
                                : (current[box.id]?.width ?? ''),
                              height: carton
                                ? safeCartonDimension(carton.external.heightUm)
                                : (current[box.id]?.height ?? ''),
                              weight: current[box.id]?.weight ?? '',
                            },
                          };
                        })
                      }
                    >
                      <option value="">Select carton type</option>
                      {cartons.map((carton) => (
                        <option key={carton.id} value={carton.id}>
                          {carton.code} — {carton.description}
                        </option>
                      ))}
                    </select>
                  </FormField>
                )}
                <Button type="button" disabled={!canFinalize} onClick={() => onFinalize(box)}>
                  Finalize dimensions and gross weight
                </Button>
              </>
            )}
            {box.status === 'finalized' && <ReopenBox box={box} onReopen={onReopen} />}
          </div>
        );
      })}
    </Card>
  );
}

function DispatchReceiptSummary({ response }: { response: DispatchResponse }) {
  return (
    <div role="status" className="rounded-md border border-positive/40 bg-positive/10 p-3">
      <p className="font-medium">Dispatch confirmed</p>
      <p className="mt-1 text-sm text-fg-secondary">
        {response.dispatch.prepId} was dispatched at{' '}
        {new Date(response.dispatch.dispatchedAt).toLocaleString()} (version{' '}
        {response.dispatch.version}).
      </p>
      {response.replayed && (
        <p className="mt-1 text-sm text-fg-secondary">This was a safe retry replay.</p>
      )}
    </div>
  );
}

function DispatchRecovery({
  request,
  pending,
  alreadyDispatched,
  onRetry,
  onError,
}: {
  request: DispatchRequest;
  pending: boolean;
  alreadyDispatched: boolean;
  onRetry: (request: DispatchRequest) => Promise<WarehousePrepDispatchResponse>;
  onError: (reason: unknown) => void;
}) {
  return (
    <div role="status" className="space-y-2 rounded-md border border-warning/40 p-3">
      <p className="font-medium">
        {alreadyDispatched
          ? 'Dispatch receipt needs recovery'
          : 'Dispatch status needs confirmation'}
      </p>
      <p className="text-sm text-fg-secondary">
        The request may have reached the server. Retry the same request to safely recover its
        immutable receipt.
      </p>
      <Button type="button" disabled={pending} onClick={() => void onRetry(request).catch(onError)}>
        {pending
          ? 'Checking dispatch status…'
          : alreadyDispatched
            ? 'Recover dispatch receipt'
            : 'Retry dispatch request'}
      </Button>
    </div>
  );
}

function Handoff({
  batch,
  boxes,
  handoff,
  loading,
  error,
  syncPending,
  confirmPending,
  retryPending,
  dispatchPending,
  dispatchResponse,
  dispatchRequest,
  batchRefreshing,
  handoffRefreshing,
  amazonEnabled,
  onRetryLoad,
  onSync,
  onConfirm,
  onRetryLabel,
  onDownloadLabel,
  onCopy,
  onDispatch,
}: {
  batch: WarehousePrepBatchDetail;
  boxes: WarehousePrepBox[];
  handoff: PrepAmazonHandoffResponse | undefined;
  loading: boolean;
  error: boolean;
  syncPending: boolean;
  confirmPending: boolean;
  retryPending: boolean;
  dispatchPending: boolean;
  dispatchResponse: DispatchResponse | undefined;
  dispatchRequest: DispatchRequest | undefined;
  batchRefreshing: boolean;
  handoffRefreshing: boolean;
  amazonEnabled: boolean;
  onRetryLoad: () => void;
  onSync: () => Promise<unknown>;
  onConfirm: (expectedVersion: number, choiceToken: string) => Promise<unknown>;
  onRetryLabel: (boxReference: string) => Promise<unknown>;
  onDownloadLabel: (boxReference: string) => Promise<unknown>;
  onCopy: () => void;
  onDispatch: (
    request: Omit<DispatchWarehousePrepBatchRequest, 'idempotencyKey'> & {
      idempotencyKey: string;
    }
  ) => Promise<WarehousePrepDispatchResponse>;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [workComplete, setWorkComplete] = useState(false);
  const [manualEvidence, setManualEvidence] = useState<
    Record<string, { applied: boolean; reason: string; externalReference: string }>
  >({});
  const [actionError, setActionError] = useState<string | null>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const run = (action: () => Promise<unknown>) => {
    setActionError(null);
    void action()
      .then(() => statusRef.current?.focus())
      .catch((reason) => {
        onRetryLoad();
        statusRef.current?.focus();
        setActionError(humanHandoffError(reason));
      });
  };
  const reconciliation = handoff?.reconciliation;
  const proposal = reconciliation?.proposal;
  const proposalIdentity = `${batch.id}:${reconciliation?.version ?? ''}:${proposal?.choiceToken ?? ''}`;
  useEffect(() => setConfirmed(false), [proposalIdentity]);
  const dispatchIdentity = `${batch.id}:${batch.version}:${reconciliation?.version ?? ''}`;
  useEffect(() => {
    setWorkComplete(false);
    setManualEvidence({});
  }, [dispatchIdentity]);
  const manualBoxes = handoff?.boxes.filter((box) => !box.label.downloadReady) ?? [];
  const manualLabelEvidence = manualBoxes.flatMap((box) => {
    const evidence = manualEvidence[box.reference];
    if (!evidence?.applied || !evidence.reason.trim()) return [];
    const externalReference = evidence.externalReference.trim();
    return [
      {
        boxReference: box.reference,
        attestedApplied: true as const,
        reason: evidence.reason.trim(),
        ...(externalReference ? { externalReference } : {}),
      },
    ];
  });
  const manualEvidenceComplete = manualBoxes.every((box) => {
    const evidence = manualEvidence[box.reference];
    return Boolean(evidence?.applied && evidence.reason.trim());
  });
  const canDispatch =
    reconciliation?.state === 'linked' &&
    batch.status !== 'dispatched' &&
    !batchRefreshing &&
    !handoffRefreshing &&
    workComplete &&
    manualEvidenceComplete &&
    !dispatchResponse;
  const dispatchKey = useWarehouseIdempotencyKey({
    action: 'prep-dispatch',
    batchId: batch.id,
    expectedVersion: batch.version,
    workComplete,
    manualLabelEvidence,
  });
  const submitDispatch = () => {
    if (!canDispatch) return;
    setActionError(null);
    void onDispatch({
      expectedVersion: batch.version,
      idempotencyKey: dispatchKey,
      workComplete: true,
      manualLabelEvidence,
    }).catch((reason) => setActionError(humanPrepError(reason)));
  };
  const canConfirm =
    proposal?.status === 'review_required' &&
    proposal.placements.length === handoff?.boxes.length &&
    new Set(proposal.placements.map((placement) => placement.prepBoxReference)).size ===
      proposal.placements.length;
  return (
    <Card className="space-y-4">
      <h3 className="font-medium">Seller Central handoff</h3>
      <p className="text-sm text-fg-secondary">
        In Seller Central, start one Send to Amazon workflow. Use Prep ID PB-… as the workflow name,
        add all items and all boxes below, then complete placement there. Do not create one workflow
        per box.
      </p>
      <p className="text-sm text-fg-secondary">
        Use {batch.reference} as the workflow name. This workflow includes {boxes.length} box
        {boxes.length === 1 ? '' : 'es'}.
      </p>
      <Button type="button" onClick={onCopy}>
        Copy packing worksheet
      </Button>
      {dispatchResponse ? (
        <DispatchReceiptSummary response={dispatchResponse} />
      ) : dispatchRequest ? (
        <DispatchRecovery
          request={dispatchRequest}
          pending={dispatchPending}
          alreadyDispatched={batch.status === 'dispatched'}
          onRetry={onDispatch}
          onError={(reason) => setActionError(humanPrepError(reason))}
        />
      ) : !amazonEnabled ? (
        <p className="text-sm text-fg-secondary" role="status">
          Amazon handoff is unavailable because the Amazon module is not enabled for this workspace.
        </p>
      ) : loading ? (
        <div className="space-y-3 border-t border-border-subtle pt-4" aria-hidden="true">
          <div className="space-y-2">
            <SkeletonLine className="w-40" />
            <SkeletonLine className="w-64 h-3" />
          </div>
          <Skeleton className="h-9 w-32 rounded-md" />
        </div>
      ) : error ? (
        <RetryCopy message="Could not load Amazon handoff." onRetry={onRetryLoad} />
      ) : !handoff ? null : (
        <div ref={statusRef} tabIndex={-1} className="space-y-3 border-t border-border-subtle pt-4">
          {actionError && (
            <p role="alert" className="text-sm text-negative">
              {actionError}
            </p>
          )}
          <div>
            <p className="font-medium">Amazon handoff status</p>
            <p className="text-sm text-fg-secondary">{handoff.statusSummary}</p>
            {handoff.destinationSummary && (
              <p className="mt-1 text-sm text-positive">{handoff.destinationSummary}</p>
            )}
            {handoff.blocker && <p className="mt-1 text-sm text-warning">{handoff.blocker}</p>}
          </div>
          {(reconciliation?.state !== 'linked' ||
            handoff.boxes.some((box) => box.label.needsIdentityCheck)) && (
            <Button type="button" disabled={syncPending} onClick={() => run(onSync)}>
              {syncPending ? 'Checking Amazon…' : 'Check Amazon'}
            </Button>
          )}
          {reconciliation?.state === 'awaiting_sync' && (
            <div className="space-y-2">
              <p className="text-sm text-fg-secondary">
                Amazon is not ready yet. Check the workflow after placement is complete in Seller
                Central.
              </p>
            </div>
          )}
          {reconciliation?.state === 'needs_confirmation' && proposal && (
            <div className="space-y-2 rounded-md border border-warning/40 p-3">
              <p className="font-medium">Amazon needs confirmation</p>
              <p className="text-sm text-fg-secondary">
                Amazon found a placement that needs a human review. Confirm only if the placement
                and box matches shown in Seller Central are correct.
              </p>
              <p className="text-sm text-warning">{proposal.reasons.join(' ')}</p>
              {canConfirm ? (
                <>
                  <div className="space-y-2 rounded border border-border-subtle p-2 text-sm">
                    <p className="font-medium">Proposed box placements</p>
                    {proposal.placements.map((placement) => (
                      <div key={placement.prepBoxReference}>
                        <p>
                          {placement.prepBoxReference} → Shipment {placement.shipmentOrdinal},
                          carton {placement.cartonOrdinal} → {placement.destinationSummary}
                        </p>
                        <p className="text-fg-secondary">
                          {placement.products
                            .map(
                              (product) =>
                                `${product.sellerSku}: ${product.amazonUnits} Amazon units`
                            )
                            .join(', ')}
                        </p>
                        {formatAmazonCartonEvidence(placement.dimensionsUm, placement.weightMg) && (
                          <p className="text-fg-secondary">
                            {formatAmazonCartonEvidence(placement.dimensionsUm, placement.weightMg)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  <label className="flex gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={confirmed}
                      onChange={(event) => setConfirmed(event.target.checked)}
                    />
                    I reviewed the proposed box-to-destination placement and confirm these matches.
                  </label>
                  <Button
                    type="button"
                    disabled={!confirmed || confirmPending || reconciliation.version === null}
                    onClick={() => {
                      if (reconciliation.version === null) return;
                      run(() => onConfirm(reconciliation.version!, proposal.choiceToken));
                    }}
                  >
                    {confirmPending ? 'Confirming Amazon placement…' : 'Confirm Amazon placement'}
                  </Button>
                </>
              ) : (
                <p className="text-sm text-warning">
                  Correct the Amazon carton evidence so that each prep box has one clear destination
                  before confirming.
                </p>
              )}
            </div>
          )}
          {reconciliation?.state === 'error' && (
            <div className="space-y-2">
              <p className="text-sm text-warning">
                Amazon handoff evidence needs attention before labels can be requested.
              </p>
            </div>
          )}
          {reconciliation?.state === 'linked' && (
            <p className="text-sm text-positive">
              Amazon placements are linked. Label status is shown per prep box.
            </p>
          )}
          {reconciliation?.state === 'linked' &&
            (batch.status === 'dispatched' ? (
              <p role="status" className="text-sm text-fg-secondary">
                This prep batch has already been dispatched. Its immutable receipt is retained by
                the server.
              </p>
            ) : batchRefreshing || handoffRefreshing ? (
              <p role="status" className="text-sm text-fg-secondary">
                Refreshing the latest prep state before dispatch.
              </p>
            ) : (
              <div className="space-y-3 rounded-md border border-border-subtle p-3">
                <div>
                  <h4 className="font-medium">Close prep work</h4>
                  <p className="mt-1 text-sm text-fg-secondary">
                    Confirm the physical work is complete. PDF-ready boxes need no manual label
                    evidence; every other box needs an applied-label attestation.
                  </p>
                </div>
                {manualBoxes.map((box) => {
                  const evidence = manualEvidence[box.reference] ?? {
                    applied: false,
                    reason: '',
                    externalReference: '',
                  };
                  return (
                    <div
                      key={box.reference}
                      className="space-y-2 rounded border border-border-subtle p-2"
                    >
                      <p className="font-medium">Manual label evidence: {box.reference}</p>
                      <label
                        htmlFor={`dispatch-${box.reference}-applied`}
                        className="flex gap-2 text-sm"
                      >
                        <input
                          id={`dispatch-${box.reference}-applied`}
                          type="checkbox"
                          checked={evidence.applied}
                          onChange={(event) =>
                            setManualEvidence((current) => ({
                              ...current,
                              [box.reference]: { ...evidence, applied: event.target.checked },
                            }))
                          }
                        />
                        I applied the label to this box.
                      </label>
                      <FormField
                        label="Why was this label applied manually?"
                        htmlFor={`dispatch-${box.reference}-reason`}
                      >
                        <Input
                          id={`dispatch-${box.reference}-reason`}
                          value={evidence.reason}
                          onChange={(event) =>
                            setManualEvidence((current) => ({
                              ...current,
                              [box.reference]: { ...evidence, reason: event.target.value },
                            }))
                          }
                        />
                      </FormField>
                      <FormField
                        label="External reference (optional)"
                        htmlFor={`dispatch-${box.reference}-external`}
                      >
                        <Input
                          id={`dispatch-${box.reference}-external`}
                          value={evidence.externalReference}
                          onChange={(event) =>
                            setManualEvidence((current) => ({
                              ...current,
                              [box.reference]: {
                                ...evidence,
                                externalReference: event.target.value,
                              },
                            }))
                          }
                        />
                      </FormField>
                    </div>
                  );
                })}
                <label htmlFor="dispatch-work-complete" className="flex gap-2 text-sm">
                  <input
                    id="dispatch-work-complete"
                    type="checkbox"
                    checked={workComplete}
                    onChange={(event) => setWorkComplete(event.target.checked)}
                  />
                  I confirm all prep work is complete and ready to leave the warehouse.
                </label>
                <Button
                  type="button"
                  disabled={!canDispatch || dispatchPending}
                  onClick={submitDispatch}
                >
                  {dispatchPending ? 'Dispatching prep batch…' : 'Dispatch prep batch'}
                </Button>
              </div>
            ))}
          <div className="space-y-3">
            <h4 className="font-medium">Prep-box labels</h4>
            {handoff.boxes.map((box) => (
              <div key={box.reference} className="rounded-md border border-border-subtle p-3">
                <p className="font-medium">Prep box {box.reference}</p>
                {box.products.length > 0 && (
                  <p className="mt-1 text-sm text-fg-secondary">
                    {box.products
                      .map((product) => `${product.sellerSku} · ${product.listingUnits} units`)
                      .join(', ')}
                  </p>
                )}
                {box.label.state === 'not_requested' && !box.label.fetchable && (
                  <p className="mt-1 text-sm text-fg-secondary">
                    Waiting for Amazon box identity. A package-only placement cannot request a label
                    yet.
                  </p>
                )}
                {box.label.state === 'not_requested' && box.label.fetchable && (
                  <div className="mt-2 space-y-2">
                    <p className="text-sm text-fg-secondary">
                      A printable Amazon box identity is linked.
                    </p>
                    <Button
                      type="button"
                      disabled={retryPending}
                      onClick={() => run(() => onRetryLabel(box.reference))}
                    >
                      {retryPending ? 'Fetching label…' : `Fetch label for ${box.reference}`}
                    </Button>
                  </div>
                )}
                {box.label.state === 'pending' && (
                  <div className="mt-2 space-y-2">
                    <p className="text-sm text-fg-secondary">Label request is pending.</p>
                    {box.label.fetchable && (
                      <Button
                        type="button"
                        disabled={retryPending}
                        onClick={() => run(() => onRetryLabel(box.reference))}
                      >
                        {retryPending ? 'Fetching label…' : `Fetch label for ${box.reference}`}
                      </Button>
                    )}
                  </div>
                )}
                {box.label.state === 'fetching' && (
                  <p className="mt-1 text-sm text-fg-secondary">Fetching label from Amazon…</p>
                )}
                {box.label.state === 'retryable_error' && (
                  <div className="mt-2 space-y-2">
                    <p role="alert" className="text-sm text-negative">
                      Label download failed. Retry the label.
                    </p>
                    <Button
                      type="button"
                      disabled={retryPending}
                      onClick={() => run(() => onRetryLabel(box.reference))}
                    >
                      {retryPending ? 'Retrying label…' : `Retry label for ${box.reference}`}
                    </Button>
                  </div>
                )}
                {box.label.downloadReady && (
                  <button
                    type="button"
                    className="mt-2 text-sm text-accent-fg hover:underline focus:outline-none focus:ring-2 focus:ring-accent-primary"
                    onClick={() => run(() => onDownloadLabel(box.reference))}
                  >
                    Download label for {box.reference}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function ReopenBox({
  box,
  onReopen,
}: {
  box: WarehousePrepBox;
  onReopen: (box: WarehousePrepBox, reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  return (
    <div className="space-y-2">
      <p className="text-sm text-fg-secondary">
        Finalized {box.finalization?.packingStatus === 'provisional' ? 'provisional' : 'confirmed'}{' '}
        box. Reopening keeps a clear audit reason.
      </p>
      <FormField label="Reason to reopen" htmlFor={`reopen-${box.id}`}>
        <Input
          id={`reopen-${box.id}`}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </FormField>
      <label className="flex gap-2 text-sm">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
        />
        I confirm this box needs to be reopened.
      </label>
      <Button
        type="button"
        disabled={!reason.trim() || !confirmed}
        onClick={() => onReopen(box, reason.trim())}
      >
        Reopen box
      </Button>
    </div>
  );
}

function RecommendationPanel({
  recommendation,
  isPending,
  onCompare,
}: {
  recommendation: WarehouseCartonRecommendationResponse | undefined;
  isPending: boolean;
  onCompare: () => void;
}) {
  return (
    <Card className="space-y-3">
      <h3 className="font-medium">Carton comparison</h3>
      <p className="text-sm text-fg-secondary">
        Compare a read-only baseline with the mixed-carton option. Review the physical result before
        creating or changing any box.
      </p>
      <Button type="button" disabled={isPending} onClick={onCompare}>
        Compare carton options
      </Button>
      {recommendation?.status === 'blocked' && (
        <ul className="list-disc pl-5 text-sm text-warning">
          {recommendation.blockers.map((blocker) => (
            <li key={`${blocker.code}-${blocker.lineId ?? ''}`}>{blocker.message}</li>
          ))}
        </ul>
      )}
      {recommendation?.status === 'ready' && (
        <div className="space-y-2 text-sm text-fg-secondary">
          <p>
            Baseline: {recommendation.baseline.cartons.length} cartons ·{' '}
            {recommendation.baseline.confidence} confidence.
          </p>
          <p>
            Recommended mixed option: {recommendation.optimized.cartons.length} cartons · saves{' '}
            {Number(recommendation.savingsCents) / 100}.
          </p>
          {(recommendation.baseline.provisional || recommendation.optimized.provisional) && (
            <p className="text-warning">
              Provisional physical evidence: verify dimensions and weight before finalizing.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

function ChargePanel({
  batch,
  boxes,
  services,
  pending,
  onSubmit,
}: {
  batch: WarehousePrepBatchDetail;
  boxes: WarehousePrepBox[];
  services: Array<{
    id: string;
    code: string;
    description: string;
    billingBasis: string;
    accountingTreatment?: string;
    active: boolean;
  }>;
  pending: boolean;
  onSubmit: (input: CreateWarehousePrepWorkChargeRequest) => Promise<unknown>;
}) {
  const [serviceId, setServiceId] = useState('');
  const [boxId, setBoxId] = useState('');
  const [reason, setReason] = useState('');
  const [selectedUnits, setSelectedUnits] = useState<Record<string, string>>({});
  const [allocationBasis, setAllocationBasis] = useState<'vendor_unit' | 'listing_unit'>(
    'vendor_unit'
  );
  const [attempt, setAttempt] = useState(0);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const service = services.find((candidate) => candidate.id === serviceId);
  const finalized = boxes.filter((box) => box.status === 'finalized');
  const finalizedContents = finalized.flatMap((box) =>
    box.contents.map((content) => ({ box, content }))
  );
  const selectedBasis = service?.billingBasis === 'fixed' ? allocationBasis : service?.billingBasis;
  const selectedRows = finalizedContents.map(({ content }) => {
    const raw = selectedUnits[content.id] ?? '';
    const vendorUnits = /^\d+$/.test(raw) ? Number(raw) : null;
    return { content, raw, vendorUnits };
  });
  const selectedContents = selectedRows.flatMap(({ content, vendorUnits }) =>
    vendorUnits && vendorUnits > 0 ? [{ content, vendorUnits }] : []
  );
  const hasNonblankSelection = selectedRows.some(({ raw }) => raw.trim().length > 0);
  const selectionValid =
    hasNonblankSelection &&
    selectedRows.every(
      ({ content, raw, vendorUnits }) =>
        !raw.trim() ||
        (vendorUnits !== null &&
          Number.isSafeInteger(vendorUnits) &&
          vendorUnits > 0 &&
          vendorUnits <= content.vendorUnits &&
          (selectedBasis !== 'listing_unit' || vendorUnits % content.unitsPerListing === 0))
    );
  const semantic = {
    serviceId,
    boxId,
    reason: reason.trim(),
    batchVersion: batch.version,
    allocationBasis,
    selected: Object.entries(selectedUnits).sort(([a], [b]) => a.localeCompare(b)),
  };
  const idempotencyKey = useWarehouseIdempotencyKey({ action: 'prep-charge', semantic, attempt });

  async function submit() {
    setSelectionError(null);
    if (!service) return;
    if (service.billingBasis === 'box') {
      const box = finalized.find((item) => item.id === boxId);
      if (!box) return;
      try {
        await onSubmit({
          serviceId: service.id,
          idempotencyKey,
          reason: reason.trim(),
          sourceType: 'prep_box',
          sourceBoxId: box.id,
          expectedBatchVersion: batch.version,
          expectedBoxVersion: box.version,
          quantityMillis: 1000,
        });
        setAttempt((value) => value + 1);
      } catch {
        // Keep the semantic key so a lost response or retry replays safely.
      }
      return;
    }
    if (!selectionValid) {
      setSelectionError(
        'Enter affected whole vendor-unit counts within each finalized content. Listing-unit services must use complete packs.'
      );
      return;
    }
    const quantityMillis =
      service.billingBasis === 'vendor_unit'
        ? selectedContents.reduce((total, item) => total + item.vendorUnits * 1000, 0)
        : service.billingBasis === 'listing_unit'
          ? selectedContents.reduce(
              (total, item) => total + (item.vendorUnits / item.content.unitsPerListing) * 1000,
              0
            )
          : 1000;
    const fixedAllocation =
      service.billingBasis === 'fixed' && service.accountingTreatment === 'capitalizable'
        ? { allocationBasis }
        : {};
    try {
      await onSubmit({
        serviceId: service.id,
        idempotencyKey,
        reason: reason.trim(),
        sourceType: 'prep_batch',
        expectedBatchVersion: batch.version,
        quantityMillis,
        ...fixedAllocation,
        selectedContents: selectedContents.map(({ content, vendorUnits }) => ({
          boxContentId: content.id,
          prepLineId: content.prepLineId,
          vendorUnits,
        })),
      });
      setAttempt((value) => value + 1);
    } catch {
      // Keep the semantic key so a lost response or retry replays safely.
    }
  }
  return (
    <Card className="space-y-3">
      <h3 className="font-medium">Record prep work</h3>
      <p className="text-sm text-fg-secondary">
        Only active box, vendor-unit, listing-unit, and fixed services are available here.
      </p>
      <FormField label="Service" htmlFor="prep-service">
        <select
          id="prep-service"
          value={serviceId}
          onChange={(event) => setServiceId(event.target.value)}
          className="w-full rounded-md border border-border-subtle bg-surface px-3 py-2 text-sm"
        >
          <option value="">Select service</option>
          {services
            .filter(
              (item) =>
                item.active &&
                ['box', 'vendor_unit', 'listing_unit', 'fixed'].includes(item.billingBasis)
            )
            .map((item) => (
              <option key={item.id} value={item.id}>
                {item.code} — {item.description}
              </option>
            ))}
        </select>
      </FormField>
      <FormField label="Work or material note" htmlFor="prep-charge-reason">
        <Input
          id="prep-charge-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </FormField>
      {service?.billingBasis === 'box' ? (
        <FormField label="Finalized box" htmlFor="prep-charge-box">
          <select
            id="prep-charge-box"
            value={boxId}
            onChange={(event) => setBoxId(event.target.value)}
            className="w-full rounded-md border border-border-subtle bg-surface px-3 py-2 text-sm"
          >
            <option value="">Select finalized box</option>
            {finalized.map((box) => (
              <option key={box.id} value={box.id}>
                {box.boxNumber}
              </option>
            ))}
          </select>
        </FormField>
      ) : (
        <fieldset>
          <legend className="text-sm font-medium">Affected finalized contents</legend>
          {service?.billingBasis === 'fixed' && service.accountingTreatment === 'capitalizable' && (
            <FormField label="Allocate fixed charge by" htmlFor="prep-fixed-allocation-basis">
              <select
                id="prep-fixed-allocation-basis"
                value={allocationBasis}
                onChange={(event) =>
                  setAllocationBasis(event.target.value as 'vendor_unit' | 'listing_unit')
                }
                className="w-full rounded-md border border-border-subtle bg-surface px-3 py-2 text-sm"
              >
                <option value="vendor_unit">Vendor units</option>
                <option value="listing_unit">Listing units</option>
              </select>
            </FormField>
          )}
          {finalizedContents.map(({ content }) => (
            <FormField
              key={content.id}
              label={`${content.sellerSku} — affected vendor units (max ${content.vendorUnits})`}
              htmlFor={`prep-content-${content.id}`}
            >
              <Input
                id={`prep-content-${content.id}`}
                type="number"
                min="0"
                max={content.vendorUnits}
                step="1"
                value={selectedUnits[content.id] ?? ''}
                onChange={(event) =>
                  setSelectedUnits((current) => ({ ...current, [content.id]: event.target.value }))
                }
              />
            </FormField>
          ))}
        </fieldset>
      )}
      {selectionError && (
        <p role="alert" className="text-sm text-negative">
          {selectionError}
        </p>
      )}
      <Button
        type="button"
        disabled={
          pending ||
          !service ||
          !reason.trim() ||
          (service.billingBasis === 'box' ? !boxId : !hasNonblankSelection)
        }
        onClick={() => void submit()}
      >
        {pending ? 'Recording prep work…' : 'Record prep work'}
      </Button>
    </Card>
  );
}

function ChargeHistory({
  charges,
  pending,
  onReverse,
}: {
  charges: WarehouseWorkChargeResponse[];
  pending: boolean;
  onReverse: (chargeId: string, idempotencyKey: string, reason: string) => void;
}) {
  const [reasonByCharge, setReasonByCharge] = useState<Record<string, string>>({});
  const [confirmed, setConfirmed] = useState<Record<string, boolean>>({});
  const reversedChargeIds = new Set(
    charges.flatMap(({ charge }) => (charge.reversalOfChargeId ? [charge.reversalOfChargeId] : []))
  );
  const [reverseKeys] = useState(() => new Map<string, string>());
  return (
    <Card className="space-y-3">
      <h3 className="font-medium">Current batch work-charge history</h3>
      {charges.length === 0 ? (
        <p className="text-sm text-fg-secondary">No prep charges recorded for this batch.</p>
      ) : (
        charges.map(({ charge }) => {
          const reason = reasonByCharge[charge.id] ?? '';
          const mayReverse =
            !charge.reversalOfChargeId &&
            !reversedChargeIds.has(charge.id) &&
            !charge.activeInvoice;
          return (
            <div key={charge.id} className="rounded-md border border-border-subtle p-3 space-y-2">
              <p className="text-sm font-medium">
                {charge.code} · {charge.description}
              </p>
              <p className="text-sm text-fg-secondary">
                {charge.currency} {(charge.amountCents / 100).toFixed(2)}
              </p>
              {charge.allocationStatus === 'blocked' && (
                <p className="text-sm text-warning">
                  Charge is blocked:{' '}
                  {charge.blockReason?.replaceAll('_', ' ') ?? 'allocation needs review'}
                </p>
              )}
              {mayReverse ? (
                <>
                  <FormField label="Reason to reverse" htmlFor={`reverse-${charge.id}`}>
                    <Input
                      id={`reverse-${charge.id}`}
                      value={reason}
                      onChange={(event) =>
                        setReasonByCharge((current) => ({
                          ...current,
                          [charge.id]: event.target.value,
                        }))
                      }
                    />
                  </FormField>
                  <label className="flex gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(confirmed[charge.id])}
                      onChange={(event) =>
                        setConfirmed((current) => ({
                          ...current,
                          [charge.id]: event.target.checked,
                        }))
                      }
                    />
                    I confirm this charge should be reversed.
                  </label>
                  <Button
                    type="button"
                    disabled={pending || !reason.trim() || !confirmed[charge.id]}
                    onClick={() => {
                      const semantic = `${charge.id}\u0000${reason.trim()}`;
                      const idempotencyKey = reverseKeys.get(semantic) ?? createIdempotencyKey();
                      reverseKeys.set(semantic, idempotencyKey);
                      onReverse(charge.id, idempotencyKey, reason.trim());
                    }}
                  >
                    {pending ? 'Reversing charge…' : 'Reverse charge'}
                  </Button>
                </>
              ) : (
                <p className="text-sm text-fg-secondary">
                  This charge cannot be reversed here because it is already reversed or invoiced.
                </p>
              )}
            </div>
          );
        })
      )}
    </Card>
  );
}
